import axios from 'axios'
import SERVICE_CONSTANTS from '@/constants/services'
import {
  clearReloadAttempt,
  isActiveProfileChanged,
  isReloadBudgetExhausted,
  markReloadAttempt
} from '@/services/base/active-profile'
import Router from '@/router/index'
import Store from '@/store/index'
import Cookies from 'js-cookie'
import jwtDecode from 'jwt-decode'
import moment from 'moment'

enum Statuses {
  unauthorized = 401
}

let refreshPromise: Promise<void> | null = null
// Ставится, когда refresh окончательно провалился и мы уводим на логин.
// Гасит остальные параллельные 401 в этой же «волне», чтобы не долбить refresh.
let sessionInvalid = false

/**
 * Ручки, которые в «несошедшемся» контексте гарантированно отдают 401 с кодом смены профиля.
 * Успех по любой из них = контекст корректен, отметку о перезагрузке можно снимать.
 */
const isProfileContextUrl = (url?: string): boolean =>
  Boolean(url) && (String(url).includes('/token/ping/') || String(url).includes('/user/profiles/'))

const checkServerError = (error: any): boolean => {
  // Сюда попадают и ошибки БЕЗ ответа (обрыв сети, таймаут, отменённый запрос). Обращение к
  // `error.response.status` напрямую роняло сам обработчик: вместо исходной ошибки наружу уходил
  // TypeError, маскируя причину и ломая обработку у вызывающего кода.
  const status = error.response?.status
  return typeof status === 'number' && status >= 500 && status <= 526
}

const a = axios.create({
  baseURL: SERVICE_CONSTANTS.BASE_URL,
  withCredentials: true
})

const resetJWTR = (): void => {
  Cookies.remove('jwtr')
}

const goToLogin = (): void => {
  // Сессия недействительна (refresh тоже вернул 401) — уводим на логин.
  sessionInvalid = true
  resetJWTR()
  // Сбрасываем «залипший» флаг аутентификации: иначе Main.vue (страница логина) видит
  // getIsAuthenticated=true (данные загрузились, пока сессия была жива) и редиректит
  // обратно в /id → кабинет снова шлёт запросы → петля SignIn↔/id.
  Store.commit('user/setIsAuthenticated', false)
  if (Router.currentRoute.name !== 'SignIn') {
    Router.replace({ name: 'SignIn' }).catch(() => { /* NavigationDuplicated при гонке 401 */ })
  }
}

async function tryRefresh (): Promise<void> {
  const token = Cookies.get('jwtr')
  if (token) {
    const refreshResponse = await a.post(SERVICE_CONSTANTS.BASE_URL + '/token/refresh/', {
      refresh: token
    })
    if (refreshResponse.status === undefined) {
      console.error('refresh response status error', refreshResponse)
    }
    if (Boolean(refreshResponse.status) && refreshResponse.status === 200 && refreshResponse.data.refresh) {
      const refresh = refreshResponse.data.refresh
      const location = window.location
      const host = location.hostname.split('.').reverse()[1] + '.' + location.hostname.split('.').reverse()[0]
      Cookies.set('jwtr', refresh, {
        expires: moment.unix((jwtDecode(refresh) as any).exp).toDate(),
        domain: '.' + host
      })
      return Promise.resolve()
    } else resetJWTR()
  } else resetJWTR()

  return Promise.reject(new Error())
}

a.interceptors.response.use((response) => {
  // Любой успешный ответ означает, что сессия снова жива (напр. после повторного логина) —
  // снимаем флаг, чтобы 401-обработка снова работала штатно.
  sessionInvalid = false
  // ⚠️ Отметку о перезагрузке снимаем ТОЛЬКО по профиль-зависимым ручкам. По любому 200 её снимать
  // нельзя: после перезагрузки успешные (в т.ч. публичные) запросы приходят раньше повторного 401,
  // и лимит стал бы недостижим — клиент зациклился бы на reload↔401. А эти ручки в несошедшемся
  // контексте сами отдают 401, поэтому подтвердить сходимость по ним ложно невозможно.
  // Если же не снимать вовсе, счётчик копит УДАЧНЫЕ переключения и на третьем за минуту уводит
  // живого пользователя на логин (тот же дефект нашёлся e2e в трейд-фронтах, сценарий G7).
  if (isProfileContextUrl(response.config?.url)) clearReloadAttempt()
  return Promise.resolve(response)
}, async (error) => {
  if (error.response?.status === Statuses.unauthorized) {
    // Сессия уже признана мёртвой (идём на логин) — не пытаемся снова рефрешить.
    if (sessionInvalid) {
      return Promise.reject(error)
    }
    // Профиль переключили (здесь или на другом устройстве). Тихий retry тут недопустим: данные
    // придут от нового профиля, а сторы и UI останутся от старого. Нужна жёсткая перезагрузка
    // в новый контекст — и только после того, как токен обновлён.
    if (isActiveProfileChanged(error)) {
      if (isReloadBudgetExhausted()) {
        // Перезагружались уже дважды в пределах окна, а профиль в токене всё равно «чужой» →
        // механизм не сработал. Худший случай лестницы: уводим на логин, а не крутим reload.
        clearReloadAttempt()
        goToLogin()
        return Promise.reject(error)
      }
      try {
        if (refreshPromise === null) {
          refreshPromise = tryRefresh().finally(() => { refreshPromise = null })
        }
        await refreshPromise
        markReloadAttempt()
        window.location.reload()
        // Страница уходит на перезагрузку — не резолвим и не реджектим, чтобы не сыпать
        // ошибками в UI из in-flight запросов.
        return new Promise(() => { /* никогда не завершается: идёт reload */ })
      } catch (e) {
        goToLogin()
        return Promise.reject(error)
      }
    }
    // 401 на самом refresh → сессия мертва, обрабатываем во внешнем catch (goToLogin).
    if (error.config?.url?.includes('/token/refresh/') === true) {
      return Promise.reject(error)
    }
    // Была ли вообще живая сессия на момент 401. Анонимный пользователь (регистрация,
    // подтверждение почты, восстановление пароля) сессии не имеет — его нельзя насильно
    // уводить на логин фоновым 401 (напр. от App.vue → user/loadUser), иначе флоу установки
    // пароля выкидывает на /signin вместо страницы установки пароля. Уводим на логин только
    // когда refresh реально был и провалился (истёкшая/отозванная сессия).
    const hadSession = Boolean(Cookies.get('jwtr'))
    if (refreshPromise === null) {
      refreshPromise = tryRefresh().finally(() => { refreshPromise = null })
    }
    try {
      await refreshPromise
      return a.request(error.config) // refresh успешен → повторяем исходный запрос
    } catch (e) {
      if (hadSession) {
        goToLogin() // была живая сессия, refresh не удался → на логин, без зацикливания
      }
      return Promise.reject(error)
    }
  } else if (checkServerError(error)) {
    Router.replace({ name: 'NamedErrorPage', params: { code: error.response.status } })
  } else return Promise.reject(error)
})

// @ts-ignore
export default a
